import React, { ChangeEvent, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { SaveToLocalStorage } from "../../helpers/LocalStorage";
import Footer from "../../components/Footer";
import { VerifyToken } from "../../api-requests/Get";
import {
  loadRecaptchaScript,
  unloadRecaptchaScript,
} from "../../helpers/Recapture";
import { config } from "../../config/config";

declare const grecaptcha: any;

const Login = () => {
  const formRef = useRef(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remeberMe, setRememberMe] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errors, setErrors] = useState("");

  const siteKey = config.RECAPTURE_SITE_KEY;

  useEffect(() => {
    if (siteKey) {
      loadRecaptchaScript(siteKey)
        .then(() => {
          console.log("reCAPTCHA script loaded for login");
        })
        .catch((err) => {
          console.error("Failed to load reCAPTCHA script:", err);
        });
    }

    return () => {
      unloadRecaptchaScript();
    };
  }, [siteKey]);

  const getCookie = (name) => {
    const cookies = document.cookie.split("; ");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].split("=");
      if (cookie[0] === name) {
        return decodeURIComponent(cookie[1]);
      }
    }
    return null;
  };

  const deleteCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  const handleRemeberMeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked);
  };

  const handleSubmit = async (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      // reCAPTCHA verification
      if (window.grecaptcha && siteKey) {
        const token = await window.grecaptcha.execute(siteKey);
        const isValid = await VerifyToken(token);
        if (!isValid) {
          setErrors("Security verification failed. Please try again.");
          setIsError(true);
          return;
        }
      } else {
        console.warn("reCAPTCHA not available for login");
      }

      const response = await fetch("/api/login/password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: email,
          password: password,
          remember: remeberMe,
        }),
      });

      if (response.status === 200) {
        const buyval = getCookie("ral");
        if (buyval) {
          deleteCookie("ral");
          formRef.current.submit();
        } else {
          const data = await response.json();
          //console.log("Login data", data);
          SaveToLocalStorage("exp", data.expires);
          window.location.href = "/";
        }
      } else {
        const data = await response.json();
        setIsError(true);
        setErrors(data.message);
      }
    } catch (error) {
      setIsError(true);
      setErrors("An error occurred while logging in.");
    }
  };

  return (
    <>
      <div className="flex h-screen flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 m-6 sm:m-auto">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/">
            <img
              className="mx-auto h-10 w-auto"
              src="/img/logo.png"
              alt="10GOATS"
            />
          </Link>
          <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-white">
            Sign in to your account
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow rounded-md sm:rounded-lg sm:px-12">
            {isError && (
              <>
                <div className="border-2 border-red p-4 bg-rose-500 text-black font-semibold text-sm mb-5 rounded-md">
                  {errors}
                </div>
              </>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Email address
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Email address"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Password"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    checked={remeberMe}
                    onChange={handleRemeberMeChange}
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-3 block text-sm leading-6 text-gray-900"
                  >
                    Remember me
                  </label>
                </div>

                <div className="text-sm leading-6">
                  <Link
                    className="font-semibold text-indigo-500 hover:text-indigo-500"
                    to="/forgot-password"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Sign in
                </button>
              </div>

              {/* reCAPTCHA container */}
              <div id="recaptcha-container" style={{ display: "none" }}></div>
            </form>
          </div>

          <p className="mt-10 text-center text-sm text-white">
            Not a member?{" "}
            <Link
              className="font-semibold text-sky-500 hover:text-gray-100"
              to="/signup"
            >
              Create an account
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Login;
